import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { kycPortalService } from '../../services/kycPortalService';
import KYCPortalShell from './KYCPortalShell';
import PasswordInput from '../ui/PasswordInput';

const MIN_PASSWORD_LENGTH = 10;

/** Live password-strength check — same rule set as the new-user creation flow. */
const validatePassword = (pw) => {
    const s = {
        length: pw.length >= MIN_PASSWORD_LENGTH,
        hasUpper: /[A-Z]/.test(pw),
        hasLower: /[a-z]/.test(pw),
        hasNumber: /\d/.test(pw),
        hasSpecial: /[!@#$%^&*(),.?":{}|<>]/.test(pw),
    };
    s.valid = s.length && s.hasUpper && s.hasLower && s.hasNumber && s.hasSpecial;
    return s;
};

/** Invite acceptance: validate the token, let the partner set their password. */
const KYCPortalInvite = () => {
    const history = useHistory();
    const { token } = useParams();
    const [invite, setInvite] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [fullName, setFullName] = useState('');
    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const run = async () => {
            try {
                const data = await kycPortalService.validateInvite(token);
                setInvite(data);
                setFullName(data.full_name || '');
            } catch (err) {
                setError(err.response?.data?.error || 'This invite link is invalid or has expired.');
            } finally {
                setLoading(false);
            }
        };
        run();
    }, [token]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setError('');
        if (!validatePassword(password).valid) {
            setError('Please meet all the password requirements below.');
            return;
        }
        if (password !== confirm) {
            setError('Passwords do not match.');
            return;
        }
        setSubmitting(true);
        try {
            await kycPortalService.acceptInvite({ token, fullName: fullName.trim(), password });
            history.push('/kyc-portal');
        } catch (err) {
            setError(err.response?.data?.error || 'Could not activate your account.');
        } finally {
            setSubmitting(false);
        }
    };

    const strength = validatePassword(password);
    const passwordsMismatch = confirm.length > 0 && password !== confirm;
    const canActivate = strength.valid && password === confirm;

    return (
        <KYCPortalShell subtitle="Account Activation">
            <div className="kyc-portal-auth-wrap">
                <div className="kyc-portal-auth-card">
                    {loading ? (
                        <p className="kyc-portal-help">Checking your invitation…</p>
                    ) : !invite ? (
                        <>
                            <h2>Invitation not valid</h2>
                            <div className="kyc-portal-error">{error}</div>
                            <button
                                type="button"
                                className="kyc-portal-btn-secondary"
                                onClick={() => history.push('/kyc-portal/login')}
                            >
                                Go to Sign In
                            </button>
                        </>
                    ) : (
                        <>
                            <h2>Welcome to IMT KYC</h2>
                            <p className="kyc-portal-help">
                                You have been invited to complete the {invite.year} KYC onboarding
                                for <strong>{invite.company_name}</strong> ({invite.email}).
                                Set a password to get started.
                            </p>
                            {error && <div className="kyc-portal-error">{error}</div>}
                            <form onSubmit={handleSubmit}>
                                <div className="kyc-portal-field">
                                    <label>Your Full Name</label>
                                    <input
                                        type="text"
                                        value={fullName}
                                        onChange={(event) => setFullName(event.target.value)}
                                        placeholder="e.g. Hans Mueller"
                                    />
                                </div>
                                <div className="kyc-portal-field">
                                    <label>Password (min. {MIN_PASSWORD_LENGTH} characters)</label>
                                    <PasswordInput
                                        value={password}
                                        onChange={(event) => setPassword(event.target.value)}
                                        autoComplete="new-password"
                                    />
                                </div>
                                {password && (
                                    <div className="kyc-portal-password-reqs">
                                        <h4>Password must have:</h4>
                                        <ul>
                                            <li className={strength.length ? 'kyc-portal-req-met' : 'kyc-portal-req-missing'}>
                                                At least {MIN_PASSWORD_LENGTH} characters
                                            </li>
                                            <li className={strength.hasUpper ? 'kyc-portal-req-met' : 'kyc-portal-req-missing'}>
                                                At least one uppercase letter (A–Z)
                                            </li>
                                            <li className={strength.hasLower ? 'kyc-portal-req-met' : 'kyc-portal-req-missing'}>
                                                At least one lowercase letter (a–z)
                                            </li>
                                            <li className={strength.hasNumber ? 'kyc-portal-req-met' : 'kyc-portal-req-missing'}>
                                                At least one number (0–9)
                                            </li>
                                            <li className={strength.hasSpecial ? 'kyc-portal-req-met' : 'kyc-portal-req-missing'}>
                                                At least one special character (!@#$%^&amp;*)
                                            </li>
                                        </ul>
                                    </div>
                                )}
                                <div className="kyc-portal-field">
                                    <label>Confirm Password</label>
                                    <PasswordInput
                                        value={confirm}
                                        onChange={(event) => setConfirm(event.target.value)}
                                        autoComplete="new-password"
                                    />
                                    {passwordsMismatch && (
                                        <p className="kyc-portal-field-hint">Passwords do not match.</p>
                                    )}
                                </div>
                                <button
                                    type="submit"
                                    className="kyc-portal-btn-primary"
                                    disabled={submitting || !canActivate}
                                    style={{ width: '100%', justifyContent: 'center' }}
                                >
                                    {submitting ? 'Activating…' : 'Activate & Continue'}
                                </button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </KYCPortalShell>
    );
};

export default KYCPortalInvite;
